import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const pretrainPipelineComponent: ComponentDef = {
  id: 'pretrain-pipeline',
  name: 'Pretrain Pipeline',
  description: 'End-to-end language model pretraining: data, tokenization, model, loss, and optimizer.',
  category: 'training',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'vocab_size', label: 'Vocab size', type: 'number', default: 32000, min: 1000, max: 200000, step: 1000 },
    { name: 'variant', label: 'Variant', type: 'select', default: 'standard', options: [
      { label: 'Standard Pretrain', value: 'standard' },
      { label: 'Multi-Stage', value: 'multi_stage' }
    ]}
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const vocab_size = Number(params.vocab_size);
    const variant = String(params.variant || 'standard');

    const g = new GraphBuilder();

    if (variant === 'multi_stage') {
      // Multi-stage pretraining: progressive context length increase
      // Stage 1: short context, large batch
      g.input('data_s1', [64]);
      g.embedding('embed_s1', 'data_s1', [vocab_size, d_model]);
      g.linear('transformer_s1', 'embed_s1', [d_model, d_model]);
      g.linear('logits_s1', 'transformer_s1', [d_model, vocab_size]);
      g.input('targets_s1', [64]);
      g.loss('loss_s1', 'logits_s1', 'targets_s1');

      // Linear projection to transfer knowledge between stages
      g.linear('proj_s1_s2', 'transformer_s1', [d_model, d_model]);

      // Stage 2: medium context
      g.input('data_s2', [256]);
      g.embedding('embed_s2', 'data_s2', [vocab_size, d_model]);
      g.add('embed_s2_combined', 'embed_s2', 'proj_s1_s2');
      g.linear('transformer_s2', 'embed_s2_combined', [d_model, d_model]);
      g.linear('logits_s2', 'transformer_s2', [d_model, vocab_size]);
      g.input('targets_s2', [256]);
      g.loss('loss_s2', 'logits_s2', 'targets_s2');

      // Linear projection to transfer knowledge between stages
      g.linear('proj_s2_s3', 'transformer_s2', [d_model, d_model]);

      // Stage 3: full context, smaller batch
      g.input('data_s3', [seq_len]);
      g.embedding('embed_s3', 'data_s3', [vocab_size, d_model]);
      g.add('embed_s3_combined', 'embed_s3', 'proj_s2_s3');
      g.linear('transformer_s3', 'embed_s3_combined', [d_model, d_model]);
      g.linear('logits_s3', 'transformer_s3', [d_model, vocab_size]);
      g.input('targets_s3', [seq_len]);
      g.loss('loss_s3', 'logits_s3', 'targets_s3');

      // Combined loss from all stages
      g.add('loss_s1_s2', 'loss_s1', 'loss_s2');
      g.add('loss_total', 'loss_s1_s2', 'loss_s3');
      g.scale('optimizer', 'loss_total', 'Optimizer Step');
      g.output('out', 'optimizer');

    } else {
      // Standard single-stage pretraining
      g.input('data', [seq_len]);

      g.embedding('embed', 'data', [vocab_size, d_model]);
      g.linear('transformer', 'embed', [d_model, d_model]);
      g.linear('logits', 'transformer', [d_model, vocab_size]);
      g.input('targets', [seq_len]);
      g.loss('loss', 'logits', 'targets');
      g.scale('optimizer', 'loss', 'Optimizer Step');
      g.output('out', 'optimizer');
    }

    return g.build();
  }
};
